import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Trophy, Gamepad2, Calendar, Star, Filter } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { getApiUrl } from "@/lib/api";

const API = `${getApiUrl()}/api`;

export default function BestDayDialog({ open, onOpenChange, date, tzOffset = 0 }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !date) return;
    setLoading(true);
    axios.get(`${API}/stats/best-day-detail`, { params: { date, tz_offset: tzOffset } })
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, date, tzOffset]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#0A0A0A] border-white/10 text-white max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Calendar className="w-5 h-5 text-yellow-400" />
            Best Day — {data?.date || date}
          </DialogTitle>
          <DialogDescription className="text-white/50">
            {data ? `${data.totalCount} achievements · ${data.totalScore.toLocaleString()} points` : "Loading..."}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-white/30" /></div>
        ) : data ? (
          <div className="space-y-5 mt-2">
            {/* Games Played */}
            {data.games?.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-white/50 uppercase tracking-wider mb-2">
                  Games Played ({data.games.length})
                  {data.completions?.length > 0 && (
                    <span className="ml-2 text-emerald-400 normal-case">
                      · {data.completions.length} completed
                    </span>
                  )}
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {data.games.map(g => (
                    <Link
                      key={g.id}
                      to={`/games/${g.id}`}
                      onClick={() => onOpenChange(false)}
                      className="flex items-center gap-2.5 p-2.5 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                      data-testid={`best-day-game-${g.id}`}
                    >
                      {g.boxArtUrl ? (
                        <img src={g.boxArtUrl} alt={g.name} className="w-8 h-10 rounded object-cover bg-black/50" />
                      ) : (
                        <div className="w-8 h-10 rounded bg-white/10 flex items-center justify-center">
                          <Gamepad2 className="w-4 h-4 text-white/20" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-white text-xs font-medium truncate">{g.name}</p>
                        <p className="text-white/40 text-[10px]">
                          {g.unlockedToday} unlocked · {g.scoreToday.toLocaleString()} pts
                        </p>
                        <p className="text-white/25 text-[10px]">{g.platform} · {g.system}</p>
                      </div>
                      {data.completions?.includes(g.name) && (
                        <Star className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Achievements */}
            {data.achievements?.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-white/50 uppercase tracking-wider mb-2">
                  Achievements Unlocked ({data.achievements.length})
                </h4>
                <div className="space-y-1 max-h-72 overflow-y-auto">
                  {data.achievements.map((ach, i) => (
                    <div
                      key={ach.id || i}
                      className="flex items-center gap-2.5 py-1.5 px-2 rounded hover:bg-white/5"
                      data-testid={`best-day-ach-${i}`}
                    >
                      {ach.imageUrl ? (
                        <img src={ach.imageUrl} alt="" className="w-7 h-7 rounded object-contain bg-black/50 shrink-0" />
                      ) : (
                        <Trophy className="w-4 h-4 text-white/20 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-white text-xs font-medium truncate">{ach.name}</span>
                          {ach.score > 0 && (
                            <span className="text-[10px] px-1 py-0.5 rounded bg-yellow-500/20 text-yellow-400 shrink-0">
                              {ach.score}
                            </span>
                          )}
                        </div>
                        <p className="text-white/30 text-[10px] truncate">
                          {ach.gameName}
                          {ach.earnedTime && <span className="ml-1.5 text-white/20">at {ach.earnedTime}</span>}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Filter achievements */}
            {data.games?.length > 0 && (
              <div className="pt-2 border-t border-white/5">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full bg-white/5 border-white/10 text-white hover:bg-white/10"
                  data-testid="best-day-view-achievements"
                  onClick={() => {
                    onOpenChange(false);
                    navigate(`/achievements?status=unlocked&sortBy=earnedAt&sortOrder=asc`);
                  }}
                >
                  <Filter className="w-3.5 h-3.5 mr-1.5" />
                  Browse All Achievements
                </Button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-white/30 text-sm text-center py-8">No data available for this date.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
